import { useParams } from 'react-router-dom';
import { ROUTE_NAMES } from '@tsgecart/config';
import { Seo } from '../../../../components/seo.js';
import { Breadcrumbs } from '../../shell/components/breadcrumbs.js';
import { StorefrontErrorState, StorefrontLoadingState } from '../../components/states.js';
import { useCategory } from '../hooks/use-catalog.js';
import { ProductListing } from '../components/product-listing.js';

/**
 * Resolves the category via `useCategory(slug)` first (for the page
 * title/breadcrumb/SEO), then lists its products through the shared
 * `ProductListing`.
 */
export function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: category, isLoading, isError, refetch } = useCategory(slug);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <StorefrontLoadingState label="Loading category…" />
      </div>
    );
  }

  if (isError || !category) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <StorefrontErrorState message="Could not load this category." onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <Seo
        title={category.name}
        description={category.description ?? undefined}
        canonicalPath={`${ROUTE_NAMES.category}/${category.slug}`}
      />
      <Breadcrumbs
        items={[{ label: 'Home', to: ROUTE_NAMES.home }, { label: category.name }]}
        className="mb-4"
      />
      {category.bannerUrl && (
        <div className="mb-6 overflow-hidden rounded-2xl bg-gray-100">
          <img
            src={category.bannerUrl}
            alt=""
            className="h-32 w-full object-cover sm:h-48"
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
          />
        </div>
      )}
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">{category.name}</h1>
        {category.description && <p className="mt-2 max-w-2xl text-sm text-gray-600">{category.description}</p>}
      </div>
      <ProductListing
        baseParams={{ categoryId: category.id }}
        emptyStateDescription={`No products found in ${category.name} yet.`}
      />
    </div>
  );
}
